import {  OnDestroy } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { LoyaltyPrograms,  KeyModel } from '@models/index';
import { AmKeyboardService } from "@components/am-keyboard/am-keyboard.service";
import { ListInt } from "@interfaces/*";
import { LocalDataService } from '@app/services/local-data.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * Componente que representa un teclado virtual personalizable.
 * Este componente permite interactuar con un teclado virtual y manejar la entrada de datos en campos de formulario.
 */
@Component({
    selector: 'app-am-keyboard',
    templateUrl: './am-keyboard.component.html',
    styleUrls: ['./am-keyboard.component.scss'],
    standalone: false
})
export class AmKeyboardComponent implements OnInit, OnDestroy {
  /**
   * Tipo de teclado a mostrar (por ejemplo, 'number', 'text', 'phone', etc.).
   *
   * @type {string}
   */
  type: string;

  /**
   * Referencia al elemento HTML input asociado al teclado.
   *
   * @type {HTMLElement}
   */
  inputReference: HTMLElement;

  /**
   * Control de formulario asociado al teclado.
   *
   * @type {UntypedFormControl}
   */
  currentFormCtr: UntypedFormControl;

  /**
   * Indica si se debe mostrar la barra de navegación (anterior/siguiente).
   *
   * @type {boolean}
   * @default false
   */
  showNavigateBar: boolean = false;

  /**
   * Nombre de la lista de opciones personalizada a cargar (por ejemplo, 'loyaltyPrograms').
   *
   * @type {string}
   */
  nameList: string;

  /**
   * Indica si el teclado debe permitir valores flotantes (para campos numéricos).
   *
   * @type {boolean}
   * @default true
   */
  isFloat = true;

  /**
   * Indica si el tema del teclado es oscuro.
   *
   * @type {boolean}
   */
  isDarkTheme;

  /**
   * Tipos de entrada compatibles con el teclado.
   *
   * @type {string[]}
   */
  typesInput = ['number', 'text', 'password', 'calendar', 'phone'];

  /**
   * Tipos de entrada personalizados (por ejemplo, 'radio', 'checkbox', 'selectAm').
   *
   * @type {string[]}
   */
  typesCustom = ['radio', 'checkbox', 'selectAm'];

  /**
   * Tecla especial para ocultar el teclado.
   *
   * @type {KeyModel}
   */
  hideKey = null;

  /**
    Indica si el tema del scroll seria en blanco.
    @type {boolean}
  **/
  themeLightScroll: boolean = false;

  /**
   * Teclado QWERTY predeterminado.
   *
   * @type {Array<Array<string>>}
   */
  private keyboardQwerty = [
    ['aux', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'hide'],
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'/*, 'Ñ'*/],
    ['aux1', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'aux2', '\tr'],
    ['_', '-', 'space', '@', '.', 'aux2', 'aux2']
  ];

  /**
   * Teclado numérico predeterminado.
   *
   * @type {Array<Array<string>>}
   */
  private numberKeyboard = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['hide', '0', '\tr'],
    ['aux2', 'aux2', 'aux2', 'aux2']
  ];

  /**
   * Teclado actual cargado en el componente.
   *
   * @type {Array<Array<KeyModel>>}
   */
  keyBoard: Array<Array<KeyModel>> = [];

  /**
   * Lista de opciones personalizada cargada en el componente.
   *
   * @type {Array<any>}
   */
  optionsList: Array<any> = [];

  /**
   * Idioma actual del sistema.
   *
   * @type {string}
   */
  currentLang: string;

  /**
   * Modelo de fecha para el teclado de calendario.
   *
   * @type {NgbDateStruct}
   */
  modelDate: NgbDateStruct;

  constructor(
    private keboardServ: AmKeyboardService,
    private _localServ: LocalDataService,
    private _traslateService: TranslateService,
  ) { }
  ngOnDestroy(): void { }

  ngOnInit(): void {
    this.loadKeyboard();
    this.currentLang = this._traslateService.currentLang.replace("-", "_");
  }
  /**
   * Método que navega al siguiente campo de formulario.
   *
   * @return {void} No retorna ningún valor.
   */
  onNext() {
    const formKeys = Object.keys(this.currentFormCtr.parent.controls);
    const currentKey = formKeys.indexOf(this.inputReference.id);

    if (currentKey >= 0 && currentKey < formKeys.length) {
      let nextKey = formKeys[currentKey + 1];
      if (!nextKey) {
        nextKey = formKeys[0];
      }
      let nextHtml = document.getElementById(nextKey);
      if (!nextHtml) {
        for (let index = currentKey + 1; index < formKeys.length; index++) {
          nextKey = formKeys[index];
          nextHtml = document.getElementById(nextKey);
          if (nextHtml) {
            break;
          } else if (index == formKeys.length - 1) {
            nextKey = formKeys[0];
            nextHtml = document.getElementById(nextKey);
          }
        }
      }
      const el = document.getElementById(nextKey)
      const typeEl = el.dataset?.type || el.getAttribute('type')
      if (this.typesCustom.includes(typeEl)){ 
        this.keboardServ.destroy(true);
      }
      el.focus()
      el.click();
    }
  }

  /**
   * Método que navega al campo de formulario anterior.
   *
   * @return {void} No retorna ningún valor.
   */
  onPrev() {
    const formKeys = Object.keys(this.currentFormCtr.parent.controls);
    const currentKey = formKeys.indexOf(this.inputReference.id);

    if (currentKey >= 0 && currentKey < formKeys.length) {
      let nextKey = formKeys[currentKey - 1];
      if (!nextKey) {
        nextKey = formKeys[0];
      }
      let nextHtml = document.getElementById(nextKey);
      if (!nextHtml) {
        for (let index = currentKey - 1; index > 0; index--) {
          nextKey = formKeys[index];
          nextHtml = document.getElementById(nextKey);
          if (nextHtml) {
            break;
          } else if (index == 1) {
            nextKey = formKeys[formKeys.length - 1];
            nextHtml = document.getElementById(nextKey);
          }
        }
      }
      const el = document.getElementById(nextKey)
      const typeEl = el.dataset?.type || el.getAttribute('type')
      if (this.typesCustom.includes(typeEl)){
        this.keboardServ.destroy(true);
  
      }
      el.focus()
      el.click();
    }
  }
  /**
   * Método que maneja la acción de presionar una tecla.
   *
   * @param {string} key - Tecla presionada.
   * @return {void} No retorna ningún valor.
   */
  pressKey(key: string) {
    switch (key) {
      case '\tr':
        break;
      case '\s':
        break;
      case '\r':
        break
      default:
        break;
    }
  }
  /**
   * Getter para obtener el teclado actual.
   *
   * @return {Array<Array<KeyModel>>} Teclado actual.
   */
  get keyboard(): Array<Array<KeyModel>> {
    return this.keyBoard;
  }
/**
   * Método que deshabilita todas las teclas del teclado.
   *
   * @return {void} No retorna ningún valor.
   */
  disabledAll() {
    for (const row of this.keyBoard) {
      for (const key of row) {
        key.disabled = true;
      }
    }
    
  }
  /**
   * Método que carga el teclado correspondiente en función del tipo o lista de opciones.
   *
   * @return {void} No retorna ningún valor.
   */
  loadKeyboard() {
    if (this.nameList) {
      this.optionsList = this.getList(this.nameList);
      this.hideKey = new KeyModel('hide', 'hide', null);
    }
    else {
      switch (this.type) {
        case 'text':
          this.keyBoard = this.loadTextKeyboard()
          break;
        case 'number':
        case 'phone':
          this.keyBoard = this.loadNumberKeyboard();
          break;
        case 'calendar':
          this.keyBoard = this.loadNumberKeyboard();
          break;

        default:
          break;
      }
    }
  }

  /**
   * Método que carga un teclado de texto (QWERTY).
   *
   * @return {Array<Array<KeyModel>>} Teclado de texto cargado.
   */
  loadTextKeyboard(): Array<Array<KeyModel>> {
    let aux: Array<Array<KeyModel>> = [];
    for (const row of this.keyboardQwerty) {
      let record = [];
      for (const k of row) {
        let code = k.charCodeAt(0);
        record.push(new KeyModel(k, k, code));
      }
      aux.push(record);
    }
    return aux;
  }
  /**
   * Método que carga un teclado numérico para teléfonos.
   *
   * @return {Array<Array<KeyModel>>} Teclado numérico cargado.
   */
  loadPhoneNumberKeyboard(): Array<Array<KeyModel>> {
    let aux: Array<Array<KeyModel>> = [];
    this.numberKeyboard  = [
      ['1', '2', '3'],
      ['4', '5', '6'],
      ['7', '8', '9'],
      ['hide', '0', '\tr'],
    ];
    for(const row of this.numberKeyboard){
      let record = row.map((k: string) => {
        let code = k.charCodeAt(0);
        const key = new KeyModel(k, k, code);
        if (k === 'ABC') {
          key.disabled = true;
        }
        return key
      })
      aux.push(record);
    }
    return aux;
  }
 /**
   * Método que carga un teclado numérico estándar.
   *
   * @return {Array<Array<KeyModel>>} Teclado numérico cargado.
   */
  loadNumberKeyboard(): Array<Array<KeyModel>> {
    let aux: Array<Array<KeyModel>> = [];
    for (const row of this.numberKeyboard) {
      let record = [];
      for (const k of row) {
        let code = k.charCodeAt(0);
        const key = new KeyModel(k, k, code);
        if (k === 'ABC') {
          key.disabled = true;
        }
        record.push(key);
      }
      aux.push(record);
    }
    return aux;
  }
 /**
   * Método que carga un teclado para fechas (calendario).
   *
   * @return {Array<Array<KeyModel>>} Teclado de calendario cargado.
   */
  loadCalendarKeyboard(): Array<Array<KeyModel>> {
    let aux: Array<Array<KeyModel>> = [];
    this.numberKeyboard  = [
      ['1', '2', '3'],
      ['4', '5', '6'],
      ['7', '8', '9'],
      ['hide', '0','/', '\tr'],
      ['aux2', 'aux2', 'aux2', 'aux2']
    ];
    for (const row of this.numberKeyboard) {
      let record = [];
      for (const k of row) {
        let code = k.charCodeAt(0);
        const key = new KeyModel(k, k, code);
        if (k === 'ABC') {
          key.disabled = true;
        }
        record.push(key);
      }
      aux.push(record);
    }
    return aux;
  }
 /**
   * Método que obtiene una lista de opciones personalizada en función del nombre de la lista.
   *
   * @param {string} nameList - Nombre de la lista a cargar.
   * @return {Array<ListInt>} Lista de opciones cargada.
   */
  getList(nameList: string): Array<ListInt> {
    let list: Array<any> = [];
    switch (nameList) {
      case 'loyaltyPrograms':
        list = LoyaltyPrograms.getloyaltyProgram();
        break;
      case 'visaType':
        this._localServ.getVisaType().subscribe(res => {
          this.optionsList = res.map((item, index: number) => ({ value: item.id, description: item[this.currentLang], selected: index === 0 }))
        });
        break;
      case 'typeStay':
        this._localServ.getTimaticStay().subscribe(res => {
          this.optionsList = res.map((item, index: number) => ({ value: item.id, description: item[this.currentLang], selected: index === 0 }))
        });
        break;
        case 'typeStayUsPerson':
          this._localServ.getUsPersonStay().subscribe(res => {
            this.optionsList = res.map((item, index: number) => ({ value: item.id, description: item[this.currentLang], selected: index === 0 }))
          });
          break;
          case 'residenceUsPerson':
          this._localServ.getUsPersonStay().subscribe(res => {
            this.optionsList = res.map((item, index: number) => ({ value: item.id, description: item[this.currentLang], selected: index === 0 }))
          });
          break;
      case 'relationType':
        this._localServ.getRelationShipType().subscribe(res => {
          this.optionsList = res.map((item, index: number) => ({ value: item.id, description: item[this.currentLang], selected: index === 0 }))
        });
        break;
      case 'answersHavana':
        this._localServ.getAnswersHavanaType().subscribe(res => {
          this.optionsList = res.map((item, index: number) => ({ value: item.id, description: item[this.currentLang], selected: index === 0 }))
        });
        break;
        case 'genderList':
          if(this._localServ.gendersTypes.length == 0){
            this._localServ.getGenderType().subscribe(res => {
              this.optionsList = res.map((item, index: number) => ({ value: item.id, description: item[this.currentLang], selected: index === 0 }))
              this._localServ.gendersTypes = this.optionsList;
            });
          } else{
            setTimeout(()=> {
              this.optionsList = this._localServ.gendersTypes;
            }, 1)
          }
          break;
        case 'stayTypeList':
          this._localServ.getStayType().subscribe(res => {
            this.optionsList = res.map((item, index: number) => ({ value: item.id, description: item[this.currentLang], selected: index === 0 }))
          });
         break;
      default:
        break;
    }
    return list.map((item, index: number) => ({ value: item.id, description: item.name, selected: index === 0 }));
  }
  /**
   * Método que maneja la selección de un elemento de la lista de opciones.
   *
   * @param {ListInt} item - Elemento seleccionado.
   * @param {number} index - Índice del elemento seleccionado.
   * @return {void} No retorna ningún valor.
   */
  onSelectItem(item: ListInt, index: number) {
    switch (this.nameList) {
      case 'loyaltyPrograms':
        const findPrev = this.optionsList.find((opt: ListInt, i: number) => opt.selected === true && index !== i);
        if (findPrev) {
          findPrev.selected = false
        }
        item.selected = true;
        this.currentFormCtr.setValue(item.description);

        break;

      case 'visaType':
        this.currentFormCtr.setValue(item.description);
        break;

      case 'typeStay':
        this.currentFormCtr.setValue(item.description);
        break;

        case 'typeStayUsPerson':
          this.currentFormCtr.setValue(item.description)
          break;

      case 'relationType':
        this.currentFormCtr.setValue(item.description);
        break;
      case 'stayTypeList':
        this.currentFormCtr.setValue(item.description)
        break;
      case 'answersHavana':
        this.currentFormCtr.setValue(item.description);
        break;
      case 'genderList':
        this.currentFormCtr.setValue(item.description);
        break;
      default:
        break;
    }
    this.keboardServ.destroy(true);
  }
}
